import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesReportAPI, userAPI, settingsAPI } from '../api';
import { toast } from 'sonner';
import { ArrowLeft, Download, Printer, TrendingUp, TrendingDown, Award, Trophy } from 'lucide-react';
import { format } from 'date-fns';
import { getSalesRangeDates, localDateBoundaryIso } from '../lib/dateRanges';
import { useAuth } from '../context/AuthContext';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtPct = (n) => `${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })}%`;

const KpiCard = ({ label, value, format: f, color = 'bg-primary' }) => (
  <div className="clay-card p-5">
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="text-2xl font-outfit font-semibold mt-1">{f ? f(value) : value}</p>
  </div>
);

const Table = ({ headers, rows, testId }) => (
  <div className="clay-card overflow-hidden" data-testid={testId}>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
          <tr>{headers.map((h, i) => <th key={i} className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="text-center py-6 text-muted-foreground">No data</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30">
              {r.map((c, j) => <td key={j} className="px-4 py-3">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'previous_month', label: 'Previous Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
  { key: 'custom', label: 'Custom' }
];

const TARGET_PERIODS = {
  today: 'daily',
  yesterday: 'daily',
  week: 'weekly',
  month: 'monthly',
  previous_month: 'monthly',
  quarter: 'quarterly',
  year: 'yearly'
};

const INITIAL_MONTH_DATES = getSalesRangeDates('month');

export const SalesReportPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canExport = user?.role === 'super_admin' || user?.permissions?.includes('reports.export');
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('month');
  const [dateFrom, setDateFrom] = useState(INITIAL_MONTH_DATES.from);
  const [dateTo, setDateTo] = useState(INITIAL_MONTH_DATES.to);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [sources, setSources] = useState([]);
  const [filters, setFilters] = useState({
    user_id: '', service: '', lead_source: '', project_type: ''
  });
  const requestSequence = useRef(0);

  const selectRange = (rangeKey) => {
    setRange(rangeKey);
    if (rangeKey !== 'custom') {
      const dates = getSalesRangeDates(rangeKey);
      setDateFrom(dates.from);
      setDateTo(dates.to);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [u, s, src] = await Promise.all([
          userAPI.getAll().catch(() => ({ data: [] })),
          settingsAPI.getServices(),
          settingsAPI.getLeadSources()
        ]);
        setUsers(u.data); setServices(s.data); setSources(src.data);
      } catch {}
    })();
  }, []);

  const fetchReport = useCallback(async () => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = localDateBoundaryIso(dateFrom);
      if (dateTo) params.date_to = localDateBoundaryIso(dateTo, true);
      if (TARGET_PERIODS[range]) params.target_period = TARGET_PERIODS[range];
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await salesReportAPI.get(params);
      if (requestId === requestSequence.current) setRep(data);
    } catch (e) {
      if (requestId === requestSequence.current) toast.error(e.response?.data?.detail || 'Failed to load sales report');
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [dateFrom, dateTo, filters, range]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = async (fmt2) => {
    try {
      const params = { export_format: fmt2 };
      if (dateFrom) params.date_from = localDateBoundaryIso(dateFrom);
      if (dateTo) params.date_to = localDateBoundaryIso(dateTo, true);
      if (TARGET_PERIODS[range]) params.target_period = TARGET_PERIODS[range];
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await salesReportAPI.export(params);
      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Sales_Report.${fmt2 === 'xlsx' ? 'xlsx' : 'csv'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  const s = rep?.executive_summary || {};

  return (
    <div className="space-y-6" data-testid="sales-report-container">
      <div className="flex items-center gap-4 print:hidden">
        <button onClick={() => navigate('/reports')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-outfit font-semibold">Sales Report</h1>
          <p className="text-muted-foreground">Sale counted when agreement signed OR advance received</p>
        </div>
        {canExport && <div className="flex items-center gap-2">
          <button onClick={() => handleExport('csv')} data-testid="sales-export-csv" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm inline-flex items-center gap-1"><Download className="h-4 w-4" /> CSV</button>
          <button onClick={() => handleExport('xlsx')} data-testid="sales-export-xlsx" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm inline-flex items-center gap-1"><Download className="h-4 w-4" /> Excel</button>
          <button onClick={() => window.print()} data-testid="sales-export-print" className="px-3 py-2 bg-primary text-white rounded-lg text-sm inline-flex items-center gap-1"><Printer className="h-4 w-4" /> Print / PDF</button>
        </div>}
      </div>

      {/* Filters */}
      <div className="clay-card p-4 space-y-3 print:hidden">
        <div className="flex flex-wrap gap-2">
          {RANGES.map(r => (
            <button key={r.key} onClick={() => selectRange(r.key)} data-testid={`sales-range-${r.key}`}
              className={`px-3 py-1.5 text-sm rounded-lg ${range === r.key ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>
              {r.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          {range === 'custom' && (<>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="sales-date-from" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="sales-date-to" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
          </>)}
          <select value={filters.user_id} onChange={(e) => setFilters({ ...filters, user_id: e.target.value })} data-testid="sales-filter-user" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
            <option value="">All Employees</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={filters.service} onChange={(e) => setFilters({ ...filters, service: e.target.value })} data-testid="sales-filter-service" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
            <option value="">All Services</option>
            {services.map(sv => <option key={sv.id} value={sv.name}>{sv.name}</option>)}
          </select>
          <select value={filters.lead_source} onChange={(e) => setFilters({ ...filters, lead_source: e.target.value })} data-testid="sales-filter-source" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
            <option value="">All Sources</option>
            {sources.map(src => <option key={src.id} value={src.name}>{src.name}</option>)}
          </select>
          <select value={filters.project_type} onChange={(e) => setFilters({ ...filters, project_type: e.target.value })} data-testid="sales-filter-project-type" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
            <option value="">All Project Types</option>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
            <option value="industrial">Industrial</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div>
      ) : rep && (
        <>
          {/* Executive Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard label="Total Records" value={s.total_records} />
            <KpiCard label="Total Revenue" value={s.total_revenue} format={fmt} />
            <KpiCard label="Open Pipeline" value={s.total_pending} format={fmt} />
            <KpiCard label="Completed" value={s.total_completed} />
            <KpiCard label="Lost" value={s.total_lost} />
            <KpiCard label="Conversion %" value={`${s.conversion_pct}%`} />
          </div>

          {/* Target and revenue performance */}
          <div>
            <h2 className="text-xl font-outfit font-medium mb-3">Target & Revenue Performance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sales-target-summary">
              <KpiCard label="Target" value={s.revenue_target} format={fmt} />
              <KpiCard label="Achieved Revenue" value={s.total_revenue} format={fmt} />
              <KpiCard label="Remaining" value={s.remaining_target} format={fmt} />
              <KpiCard label="Achievement %" value={s.target_achievement_pct} format={fmtPct} />
              <KpiCard label="Deals Closed" value={s.deals_closed || 0} />
              <KpiCard label="Expected Pipeline" value={s.expected_pipeline} format={fmt} />
              <KpiCard label="Average Deal Value" value={s.average_deal_value} format={fmt} />
              <KpiCard label="Conversion Rate" value={s.conversion_pct} format={fmtPct} />
            </div>
          </div>

          {/* Company Sales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Today" value={rep.company.today_sales} format={fmt} />
            <KpiCard label="Weekly" value={rep.company.weekly_sales} format={fmt} />
            <KpiCard label="Monthly" value={rep.company.monthly_sales} format={fmt} />
            <KpiCard label="Quarterly" value={rep.company.quarterly_sales} format={fmt} />
            <KpiCard label="Yearly" value={rep.company.yearly_sales} format={fmt} />
            <KpiCard label="Total Revenue" value={rep.company.total_revenue} format={fmt} />
            <KpiCard label="Closed Projects" value={rep.company.total_closed_projects} />
            <KpiCard label="Running Projects" value={rep.company.total_running_projects} />
          </div>

          {/* User-wise */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-outfit font-medium">User-wise Sales</h2>
              <div className="flex items-center gap-3 text-sm">
                {rep.top_performer && <span className="inline-flex items-center gap-1 text-green-600"><Trophy className="h-4 w-4" /> Top: {rep.top_performer}</span>}
                {rep.lowest_performer && <span className="inline-flex items-center gap-1 text-red-600"><TrendingDown className="h-4 w-4" /> Lowest: {rep.lowest_performer}</span>}
              </div>
            </div>
            <Table
              testId="sales-user-wise"
              headers={['Rank', 'Employee', 'Total Leads', 'New', 'Follow-ups', 'Missed', 'Hot', 'Quotations', 'Deals', 'Revenue', 'Target', 'Achievement %', 'Remaining', 'Pipeline', 'Won', 'Lost', 'Pending', 'Avg Deal', 'Avg Close (days)', 'Conv%']}
              rows={rep.user_wise.map(u => [
                u.rank, u.name, u.total_leads || 0, u.new_leads || 0, u.follow_ups || 0, u.missed_follow_ups || 0,
                u.hot_leads || 0, u.quotations || 0, u.deals_closed, fmt(u.revenue), fmt(u.revenue_target),
                fmtPct(u.target_achievement_pct), fmt(u.remaining_target), fmt(u.expected_pipeline),
                u.won, u.lost, u.pending, fmt(u.avg_deal_size), u.avg_closing_days, fmtPct(u.conversion_pct)
              ])}
            />
          </div>

          {/* Service-wise */}
          <div>
            <h2 className="text-xl font-outfit font-medium mb-3">Service-wise Sales</h2>
            <Table
              testId="sales-service-wise"
              headers={['Service', 'Total Leads', 'Won', 'Lost', 'Revenue', 'Conversion %']}
              rows={rep.service_wise.map(s2 => [s2.name, s2.total_leads, s2.won, s2.lost, fmt(s2.revenue), `${s2.conversion_pct}%`])}
            />
          </div>

          {/* Source-wise */}
          <div>
            <h2 className="text-xl font-outfit font-medium mb-3">Lead Source Sales</h2>
            <Table
              testId="sales-source-wise"
              headers={['Source', 'Leads Received', 'Won', 'Lost', 'Revenue', 'ROI', 'Conversion %']}
              rows={rep.source_wise.map(s2 => [s2.name, s2.leads_received, s2.won, s2.lost, fmt(s2.revenue), fmt(s2.roi), `${s2.conversion_pct}%`])}
            />
          </div>

          {/* Lead-wise */}
          <div>
            <h2 className="text-xl font-outfit font-medium mb-3">Lead-wise Sales</h2>
            <Table
              testId="sales-lead-wise"
              headers={['Customer', 'Project', 'Service', 'Source', 'Employee', 'Quotation', 'Deal', 'Advance', 'Pending', 'Agreement', 'Status']}
              rows={rep.lead_wise.map(l => [
                <button key={l.lead_id} onClick={() => navigate(`/leads/${l.lead_id}`)} className="text-primary hover:underline">{l.customer_name}</button>,
                l.project_name || '—', l.service || '—', l.lead_source || '—', l.assigned_employee,
                fmt(l.quotation_amount), fmt(l.final_deal_amount), fmt(l.advance_received), fmt(l.pending_amount),
                l.agreement_date ? format(new Date(l.agreement_date), 'MMM d, yyyy') : '—',
                <span key="ps" className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 capitalize">{(l.project_status || 'not_started').replace(/_/g, ' ')}</span>
              ])}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default SalesReportPage;
